import { Injectable } from "@angular/core";
import { CellularDevice } from "@app/cellular/models/cellular-device.model";
import { ClareServicePlan, Project, ServicePlanFeatureKey } from "@app/project/model";
import { AlulaDevice } from "@app/project/model/alula-device";
import {
    ClareServiceSubscription,
    ClareServiceSubscriptionData,
    ClareServiceSubscriptionHelper,
} from "@app/project/model/clare-service-subscription";
import { AlulaDeviceDbService } from "@app/project/services/alula-device-db.service";
import { ServicePlanUtils } from "@app/project/services/service-plan-utils";
import { SnackbarActions } from "@app/root-store/actions";
import { ApiResultState, LoadingState } from "@app/root-store/api-result.state";
import { CellularDbService, CellularProviderType } from "@app/shared/databases/cellular.database.service";
import { JSONRpcDatabaseService } from "@app/shared/databases/json-rpc-database";
import { ClassImporterExporter } from "@app/shared/resources/class-importer-exporter.service";
import { ClareWSCrudHelperService } from "@app/shared/services/clare-ws-crud-helper.service";
import { DataServiceError } from "@app/shared/services/data-service-error";
import { DbCrudHelper, RowsAffected } from "@app/shared/services/db-crud-helper";
import { HttpResponseHandler } from "@app/shared/services/http-response-handler";
import { TSLoggingService } from "@app/shared/services/ts-logging.service";
import { Store } from "@ngrx/store";
import { Observable, combineLatest, of, throwError } from "rxjs";
import { catchError, exhaustMap, filter, map, switchMap, take, tap } from "rxjs/operators";
import { ChangeSubscriptionAction } from "../effects";

interface RawSubscriptionConversionResponse {
    current: ClareServiceSubscriptionData;
    previous: ClareServiceSubscriptionData;
}

interface SubscriptionConversionResponse {
    current: ClareServiceSubscription;
    previous: ClareServiceSubscription;
}

export type CreateSubscriptionPayload = Omit<ClareServiceSubscription, "id">;

@Injectable()
export class ClareSubscriptionDbService {
    private helper = ClareServiceSubscriptionHelper;
    crudHelperService: ClareWSCrudHelperService<ClareServiceSubscription, ClareServiceSubscriptionData>;

    constructor(
        private alulaDeviceDbService: AlulaDeviceDbService,
        private cellularDbService: CellularDbService,
        private equatorService: JSONRpcDatabaseService,
        private store: Store,
        private tsLoggingService: TSLoggingService,
        protected crudHelper: DbCrudHelper
    ) {
        this.crudHelperService = new ClareWSCrudHelperService(tsLoggingService, crudHelper, this.helper);
    }

    /**
     * @description Fetches the active subscription for a specific account/project
     * @param projectId
     */
    getActiveSubscriptionByProject(projectId: number): Observable<ClareServiceSubscription | undefined> {
        return this.crudHelperService.retrieve({ endpoint: "GetProjectSubscription", data: { id: projectId } }).pipe(
            map(data => {
                const subs = data.map(d => this.helper.fromDbObject(d));
                const activeSub = subs.filter(s => ServicePlanUtils.isActive(s))[0];
                // This defaults to undefined so that the reducer updates the store from null -> undefined
                // If this change was null -> null, any selectors piped with distinctUntilKeyChanged("result") would not notify subscribers
                return activeSub || subs[0] || undefined;
            })
        );
    }

    /**
     * @description Creates a subscription in the database when no current subscription exists
     * @param payload
     */
    create(payload: CreateSubscriptionPayload): Observable<ClareServiceSubscription> {
        const subscriptionData = ClassImporterExporter.exportObject(payload as Partial<ClareServiceSubscription>, this.helper.importMap);
        const body = {
            /* eslint-disable @typescript-eslint/naming-convention */
            current_subscription: null,
            new_subscription: subscriptionData,
            features: [],
            /* eslint-enable @typescript-eslint/naming-convention */
        };

        return this.crudHelper.makeRequest<RawSubscriptionConversionResponse>("NewSubscription", body).pipe(
            map(({ current }) => this.helper.fromDbObject(current)),
            catchError(DataServiceError.handleError(payload))
        );
    }

    /**
     * @description Cancels an existing subscription, creates a new subscription, updates any opt-in features,
     * unassigns the alula device from the customer (deactivates security monitoring), and deactivates the cellular
     * device with the provider
     * @param currentSubscription the existing subscription to cancel
     * @param currentServicePlan the current service plan
     * @param newSubscription the new subscription to create in the db
     * @param newServicePlan the new service plan
     * @param alulaDevice$ the alula device state
     * @param project the project to perform all this on
     * @param cellularDevice device to be deactivated, if necessary
     */
    convert(
        currentSubscription: ClareServiceSubscription,
        currentServicePlan: ClareServicePlan,
        newSubscription: CreateSubscriptionPayload,
        newServicePlan: ClareServicePlan,
        alulaDevice$: Observable<ApiResultState<AlulaDevice>>,
        cellularDevice?: CellularDevice
    ): Observable<SubscriptionConversionResponse> {
        const convertSubscription = () =>
            this.newSubscription(currentSubscription, newSubscription).pipe(
                map(result => {
                    if (!result.previous) {
                        console.error("Old subscription was not updated", currentSubscription, result);
                        throw new Error("Old subscription was not updated");
                    }

                    const previous = this.helper.fromDbObject(result.previous);
                    const current = this.helper.fromDbObject(result.current);

                    return { previous, current };
                })
            );
        return alulaDevice$.pipe(
            filter(alulaDevice => alulaDevice.callState === LoadingState.loaded),
            switchMap(alulaDevice => {
                let deactivateCell$: Observable<any> = of(null);
                let deactivateMonitoring$: Observable<any> = of(null);
                const didHaveCellular =
                    currentSubscription && this.helper.isFeatureEnabled(currentServicePlan, ServicePlanFeatureKey.CELLULAR);
                const hasCellular = newSubscription && this.helper.isFeatureEnabled(newServicePlan, ServicePlanFeatureKey.CELLULAR);
                const didHaveMonitoring =
                    currentServicePlan && this.helper.isFeatureEnabled(currentServicePlan, ServicePlanFeatureKey.SECURITY_MONITORING);
                const hasMonitoring =
                    newSubscription && this.helper.isFeatureEnabled(newServicePlan, ServicePlanFeatureKey.SECURITY_MONITORING);
                if (didHaveCellular && !hasCellular && cellularDevice?.status === "active") {
                    deactivateCell$ = this.deactivateCellularDevice(cellularDevice, cellularDevice.provider);
                }
                if (didHaveMonitoring && !hasMonitoring && alulaDevice?.result) {
                    // This is where we are disabling monitoring services
                    deactivateMonitoring$ = this.alulaDeviceDbService.unassignDeviceFromCustomer(alulaDevice.result).pipe(
                        map(() => this.alulaDeviceDbService.unregisterDevice(alulaDevice.result)),
                        catchError((err: { error: { code: number } }) => {
                            if (err && err.error.code === 11) {
                                // if the Alula device is already unassigned
                                return of("");
                            }

                            return throwError(() => err);
                        })
                    );
                }
                return combineLatest([deactivateCell$, deactivateMonitoring$]);
            }),
            take(1),
            exhaustMap(() => convertSubscription())
        );
    }

    /**
     * @description Updates a subscription and its features in the database. The features are iterated over to decide which
     * features will be kept, updated, removed or added.
     * @param subscription
     */
    update(subscription: ClareServiceSubscription): Observable<RowsAffected> {
        return this.crudHelperService.update({
            endpoint: "UpdateSubscription",
            data: subscription,
            logData: { subscription },
        });
    }

    /**
     * @description Updates the set of service permissions in the Equator server that tell the application what features are enabled.
     * Can send permissions like security_monitoring, cellular_services, etc. Currently the app shows or hides the security service based
     * on a specific permission.
     * @param {{ newServicePlan: ClareServicePlan, project: Project }} payload
     * @returns {Observable<any>} the response from the permissions update in the server
     */
    updateEquator(payload: ChangeSubscriptionAction["result"]) {
        const newFeatures = [];
        if (payload.newServicePlan?.features) {
            const arr = payload.newServicePlan.features;
            arr.forEach(feature => {
                newFeatures.push(feature.featureKey);
            });
        }
        if (payload.additionalFeatures?.length) {
            payload.additionalFeatures.forEach(feature => {
                if (feature.enable_new_feature) {
                    newFeatures.push(feature.featureKey);
                }
            });
        }

        return this.equatorService.makeJSONRpcRequest$(payload.uuid, "ClareHomePermission/updatePermissions", [newFeatures]);
    }

    /**
     * @description Creates a new subscription when there is an existing subscription on an account.
     * The database rpc function is then called to update the current subscription and create the new one.
     * @param currentSubscription the subscription to cancel
     * @param newSubscription the new subscription to create
     * @returns the db call response
     */
    private newSubscription(
        currentSubscription: ClareServiceSubscription,
        newSubscription: CreateSubscriptionPayload
    ): Observable<RawSubscriptionConversionResponse> {
        const body = {
            /* eslint-disable @typescript-eslint/naming-convention */
            current_subscription: {
                id: currentSubscription.id,
                end_date: currentSubscription.endDate,
            },
            new_subscription: {
                project_id: newSubscription.projectId,
                service_plan_id: newSubscription.servicePlanId,
                start_date: newSubscription.startDate,
                end_date: newSubscription.endDate,
            },
            features: [],
            /* eslint-enable @typescript-eslint/naming-convention */
        };
        return this.crudHelper.makeRequest<RawSubscriptionConversionResponse>("NewSubscription", body);
    }

    // TODO: This should not live in this service. This should live in the cellular area.
    /**
     * @description Deactivates a cellular device
     * @param deviceData the device to deactivate
     * @param provider the 3rd party cellular provider
     * @returns the deactivation response
     */
    private deactivateCellularDevice(deviceData: CellularDevice, provider: CellularProviderType): Observable<any> {
        const rawBodyContent = {
            iccid: deviceData.iccid,
        };

        this.tsLoggingService.logEvent(null, "cellular", "ssopgrest", "deactivate", null, rawBodyContent);

        return this.cellularDbService.deactivateDevice$(rawBodyContent, provider).pipe(
            tap(() => {
                this.store.dispatch(
                    SnackbarActions.openSnackbar({
                        message: new HttpResponseHandler(
                            null,
                            null,
                            "Cellular Device deactivation submitted. It may take up to 24 hours to complete."
                        ),
                        buttonText: null,
                        duration: 5000,
                    })
                );
            }),
            catchError((error: Error) => {
                const handledError = new HttpResponseHandler(
                    null,
                    null,
                    `Cellular Device deactivation was not successfully submitted.\n
                    Error: ${error.message}`,
                    true
                );
                this.store.dispatch(
                    SnackbarActions.openSnackbar({
                        message: handledError,
                    })
                );
                return throwError(() => error);
            })
        );
    }
}
